import type {
  PostSessionDTO,
  PostSessionParticipantsDTO,
  LbCallSession,
  GetSessionsInput,
  SessionsResult,
  SessionResultForCsv,
} from './callSession.types';
import { callSessionOGService } from './rtkquery/callSession.og';
import { Strategy, type BusinessServiceConfig } from '../core';
import { AbstractBusinessService } from '../core/abstractBusinessService';
import { getIsGraphqlRoomQueriesEnabled } from '../../utils/isGraphqlRoomQueriesEnabled';
import {
  sessionApi,
  type CreateSessionInput,
  type CallHistoryOffsetConnection,
  type CallHistorySessionForCsv,
} from '../../services';
import { getIsProvider } from '../../features/realtime/realtime.selectors';

const strategyMap = {
  postSession: Strategy.OG,
  postSessionParticipants: Strategy.OG,
  postSessionEnd: Strategy.OG,
  getSessions: Strategy.OG,
  getSessionsForCsv: Strategy.OG,
};

export type CallSessionServiceInitConfig<StoreType> = Omit<
  BusinessServiceConfig<StoreType>,
  'serviceName' | 'strategyMap'
>;

/**
 * This service refers to a "call session".
 *
 * We store some information about calls in our database.
 */
export class CallSessionService<StoreType> extends AbstractBusinessService<
  keyof typeof strategyMap,
  StoreType
> {
  constructor({ store, logger }: CallSessionServiceInitConfig<StoreType>) {
    super({ serviceName: 'CallSessionService', store, logger, strategyMap });
  }

  async postSession(
    roomId: string,
    payload: PostSessionDTO,
  ): Promise<LbCallSession> {
    if (this.getStrategy('postSession') === Strategy.OG) {
      const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

      if (isGraphqlRoomQueriesEnabled) {
        const {
          connectionInfo: _connectionInfo,
          videoProvider: _videoProvider,
          ...rest
        } = payload;
        const input = {
          roomId,
          ...rest,
        } as CreateSessionInput;
        const response = await this.dispatchAction<{
          createSession: LbCallSession;
        }>(
          sessionApi.endpoints.CreateSession.initiate({
            input,
          }),
        );

        if ('data' in response) {
          return response.data.createSession;
        } else if ('errors' in response && Array.isArray(response.errors)) {
          throw response.errors[0];
        }
      } else {
        const data = { roomId, payload };
        const response = await this.dispatchAction<LbCallSession>(
          callSessionOGService.endpoints.postSession.initiate(data),
        );
        if ('data' in response) {
          return response.data;
        } else {
          throw response.error;
        }
      }
    }
    throw new Error('Strategy not supported');
  }

  postSessionParticipants(
    sessionId: string,
    payload: PostSessionParticipantsDTO,
  ): void {
    if (this.getStrategy('postSessionParticipants') === Strategy.OG) {
      const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();
      // This is set here under the assumption that postSessionParticipants is called before postSessionEnd.

      if (isGraphqlRoomQueriesEnabled) {
        const input = { sessionId, ...payload };
        void this.dispatchAction(
          sessionApi.endpoints.AddSessionParticipant.initiate({
            input,
          }),
        );
      } else {
        const data = { sessionId, payload };
        void this.dispatchAction(
          callSessionOGService.endpoints.postSessionParticipants.initiate(data),
        );
      }
    }
  }

  async postSessionEnd(
    dbSessionId: string,
    participantsCount?: number,
    roomId?: string,
  ): Promise<void> {
    if (this.getStrategy('postSessionEnd') === Strategy.OG) {
      const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

      const data = { sessionId: dbSessionId, participantsCount };
      if (isGraphqlRoomQueriesEnabled) {
        const isProvider = getIsProvider(this.store.getState());

        if (isProvider) {
          await this.dispatchAction(
            sessionApi.endpoints.EndSessionFromProvider.initiate({
              input: data,
            }),
          );
        } else {
          await this.dispatchAction(
            sessionApi.endpoints.EndSessionFromPatient.initiate({
              input: { ...data, ...(roomId && { roomId }) },
            }),
          );
        }
      } else {
        await this.dispatchAction(
          callSessionOGService.endpoints.postSessionEnd.initiate(data),
        );
      }
    }
  }

  async getSessions(input: GetSessionsInput): Promise<SessionsResult> {
    if (this.getStrategy('getSessions') === Strategy.OG) {
      const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

      if (isGraphqlRoomQueriesEnabled) {
        const {
          offset,
          limit,
          institutionId: _institutionId,
          locationIds: _locationIds,
          ...rest
        } = input;

        const response = await this.dispatchAction<{
          callHistory: CallHistoryOffsetConnection;
        }>(
          sessionApi.endpoints.CallHistory.initiate({
            offset,
            limit,
            input: {
              ...rest,
              roomIds: rest.roomIds?.map((roomId) => roomId.toString()) || [],
              userIds: rest.userIds?.map((userId) => userId.toString()) || [],
            },
          }),
        );

        if ('data' in response) {
          const { callHistory } = response.data;

          return {
            callSessions: callHistory?.edges?.map(({ node }) => node),
            total: callHistory?.pageInfo?.totalCount ?? 0,
          };
        } else if ('errors' in response && Array.isArray(response.errors)) {
          throw response.errors[0];
        }
      } else {
        const response = await this.dispatchAction<SessionsResult>(
          callSessionOGService.endpoints.getSessions.initiate({ input }),
        );
        if ('data' in response) {
          return response.data;
        } else {
          throw response.error;
        }
      }
    }
    throw new Error('Strategy not supported');
  }

  async getSessionsForCsv(
    input: GetSessionsInput,
  ): Promise<CallHistorySessionForCsv[]> {
    if (this.getStrategy('getSessionsForCsv') === Strategy.OG) {
      const isGraphqlRoomQueriesEnabled = getIsGraphqlRoomQueriesEnabled();

      if (isGraphqlRoomQueriesEnabled) {
        const response = await this.dispatchAction<{
          callHistoryForCsv: CallHistorySessionForCsv[];
        }>(
          sessionApi.endpoints.CallHistoryForCsv.initiate({
            input: {
              start: input.start,
              end: input.end,
              roomIds: input.roomIds?.map((roomId) => roomId.toString()) || [],
              userIds: input.userIds?.map((userId) => userId.toString()) || [],
            },
          }),
        );

        if ('data' in response) {
          return response.data.callHistoryForCsv;
        } else if ('errors' in response && Array.isArray(response.errors)) {
          throw response.errors[0];
        }
      } else {
        const response = await this.dispatchAction<SessionResultForCsv[]>(
          callSessionOGService.endpoints.getSessionsForCsv.initiate({ input }),
        );
        if ('data' in response) {
          return response.data;
        } else {
          throw response.error;
        }
      }
    }
    throw new Error('Strategy not supported');
  }
}
